/**
 * Video Reviews Slider — Splide.js integration.
 * Direct port of reviews-slider.js for the .section-video-reviews block.
 * Reads perPage/gap from data-* attributes and supports fractional perPage.
 */

if (typeof window.VideoReviewsSlider === 'undefined') {
class VideoReviewsSlider {
  constructor(element) {
    this.splideElement = element || document.querySelector('.section-video-reviews .splide');
    if (this.splideElement) {
      const section = this.splideElement.closest('.section-video-reviews');
      if (section) {
        this.prevButton = section.querySelector('.video-reviews-nav-prev');
        this.nextButton = section.querySelector('.video-reviews-nav-next');
      }
    }
    this.splide = null;
    this.init();
  }

  init() {
    if (!this.splideElement) return;

    const gapDesktop = parseInt(this.splideElement.dataset.gap || '0', 10);
    const gapMobile = parseInt(this.splideElement.dataset.gapMobile || '0', 10);
    const perPageDesktopRaw = parseFloat(this.splideElement.dataset.perPage || '4');
    const perPageTabletRaw = parseFloat(this.splideElement.dataset.perPageTablet || '2');
    const perPageMobileRaw = parseFloat(this.splideElement.dataset.perPageMobile || '1');

    const slideCount =
      this.splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)').length ||
      this.splideElement.querySelectorAll('.splide__slide').length;

    const perPageDesktop = Math.min(perPageDesktopRaw, slideCount) || 1;
    const perPageTablet = Math.min(perPageTabletRaw, slideCount) || 1;
    const perPageMobile = Math.min(perPageMobileRaw, slideCount) || 1;

    const useLoop = slideCount > perPageDesktop;
    const hasDecimal = (n) => n % 1 !== 0;
    this.fractionalMode =
      hasDecimal(perPageDesktopRaw) || hasDecimal(perPageTabletRaw) || hasDecimal(perPageMobileRaw);

    this.perPageDesktop = perPageDesktop;
    this.perPageTablet = perPageTablet;
    this.perPageMobile = perPageMobile;
    this.gapDesktop = gapDesktop;
    this.gapMobile = gapMobile;

    // @ts-ignore - Splide is loaded from CDN
    this.splide = new window.Splide(this.splideElement, {
      type: useLoop ? 'loop' : 'slide',
      perPage: this.fractionalMode ? 1 : perPageDesktop,
      perMove: 1,
      autoWidth: this.fractionalMode,
      autoHeight: true,
      arrows: false,
      pagination: false,
      autoplay: false,
      transition: 'slide',
      speed: 600,
      // Gated on the perPage in effect at the CURRENT viewport, not the mobile
      // one. Recomputed on resize in toggleControls().
      drag: slideCount > this.currentPerPage(),
      touchMinDistance: 5,
      // Splide's own wheel handling reacts to vertical deltas too, which stops
      // the page scrolling once the cursor is over the carousel. Left off; a
      // sideways-only handler lives in attachHorizontalWheelGuard().
      wheel: false,
      keyboard: 'global',
      gap: gapDesktop + 'px',
      padding: 0,
      updateOnMove: true,
      trimSpace: 'move',
      breakpoints: {
        1024: { perPage: this.fractionalMode ? 1 : perPageTablet },
        768: { perPage: this.fractionalMode ? 1 : perPageMobile, gap: gapMobile + 'px' }
      }
    });

    this.slideCount = slideCount;
    this._fixing = false;
    this.splide.mount();
    this.attachHorizontalWheelGuard();
    this.attachCustomNavigation();
    this.attachScrollbar();
    this.toggleControls();
    this.fixLastSlideMargin();
    this.attachVideoClickToPlay();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.toggleControls();
        this.fixLastSlideMargin();
      }, 100);
    });
  }

  /**
   * Splide's `wheel` option reacts to vertical deltas as well as horizontal
   * ones, which would swallow normal page scrolling over the carousel. Capture
   * wheel events first and stop any gesture that is mostly vertical from
   * reaching Splide, so only sideways swipes move the slides.
   */
  attachHorizontalWheelGuard() {
    if (!this.splideElement) return;
    const track = this.splideElement.querySelector('.splide__track');
    if (!track) return;

    track.addEventListener(
      'wheel',
      (e) => {
        if (!this.splide) return;
        if (this.splideElement.classList.contains('is-fits')) return;

        // Only act on a clearly sideways two-finger swipe. Anything with a
        // meaningful vertical component is left entirely alone so the page
        // keeps scrolling as normal — the carousel never traps the cursor.
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY) * 2) return;
        if (Math.abs(e.deltaX) < 8) return;

        e.preventDefault();

        const now = Date.now();
        if (this._wheelLock && now - this._wheelLock < 350) return;
        this._wheelLock = now;

        this.splide.go(e.deltaX > 0 ? '>' : '<');
      },
      { passive: false }
    );
  }

  /**
   * The perPage value in effect at the current viewport width. Mirrors the
   * breakpoints passed to Splide (1024 tablet, 768 mobile).
   */
  currentPerPage() {
    if (window.innerWidth <= 768) return this.perPageMobile;
    if (window.innerWidth <= 1024) return this.perPageTablet;
    return this.perPageDesktop;
  }

  fixLastSlideMargin() {
    if (!this.splide || !this.splideElement) return;
    if (this._fixing) return;
    this._fixing = true;

    try {
      let effectivePerPage = this.perPageDesktop;
      let effectiveGap = this.gapDesktop;
      if (window.innerWidth <= 768) {
        effectivePerPage = this.perPageMobile;
        effectiveGap = this.gapMobile;
      } else if (window.innerWidth <= 1024) {
        effectivePerPage = this.perPageTablet;
      }

      const allSlides = this.splideElement.querySelectorAll('.splide__slide');
      if (this.fractionalMode) {
        const track = this.splideElement.querySelector('.splide__track');
        const trackWidth = track ? track.clientWidth : 0;
        if (trackWidth > 0) {
          // Splide advances the track by (width + gap) per slide — the gap is a
          // margin-right carried by EVERY slide, including the last one on screen.
          // So N visible slides consume N*(width + gap), not N*width + (N-1)*gap.
          const slideWidth = (trackWidth - effectivePerPage * effectiveGap) / effectivePerPage;
          allSlides.forEach((slide) => {
            slide.style.width = slideWidth + 'px';
          });
        }
      }

      const fits = this.slideCount <= Math.floor(effectivePerPage);
      const slides = this.splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
      slides.forEach((slide, i) => {
        const shouldZero = fits && i === slides.length - 1;
        const currentMargin = slide.style.marginRight;
        if (shouldZero) {
          if (currentMargin !== '0px' && currentMargin !== '0') {
            slide.style.marginRight = '0px';
          }
        } else if (!this.fractionalMode && currentMargin !== '') {
          slide.style.removeProperty('margin-right');
        }
      });
    } finally {
      this._fixing = false;
    }
  }

  toggleControls() {
    if (!this.splide || !this.splideElement) return;
    const section = this.splideElement.closest('.section-video-reviews');
    if (!section) return;

    // In fractional mode splide.options.perPage is pinned to 1 (autoWidth does
    // the real work), so use the breakpoint-aware value instead.
    const currentPerPage = this.currentPerPage() || 1;
    const fits = this.slideCount <= currentPerPage;

    this.splideElement.classList.toggle('is-fits', fits);

    // Keep drag in sync with the current breakpoint.
    this.splide.options = { drag: !fits };

    const bottomRow = section.querySelector('.video-reviews-bottom-row');
    const nav = section.querySelector('.video-reviews-navigation');
    const scrollbar = section.querySelector('.video-reviews-scrollbar');

    if (bottomRow instanceof HTMLElement) {
      bottomRow.style.display = fits ? 'none' : '';
    } else {
      if (nav instanceof HTMLElement) nav.style.display = fits ? 'none' : '';
      if (scrollbar instanceof HTMLElement) scrollbar.style.display = fits ? 'none' : '';
    }
  }

  attachCustomNavigation() {
    if (this.prevButton instanceof HTMLElement) {
      this.prevButton.addEventListener('click', () => {
        if (this.splide) this.splide.go('<');
      });
    }
    if (this.nextButton instanceof HTMLElement) {
      this.nextButton.addEventListener('click', () => {
        if (this.splide) this.splide.go('>');
      });
    }
    this.updateNavigationState();
    if (this.splide) {
      this.splide.on('move', () => this.updateNavigationState());
    }
  }

  updateNavigationState() {
    if (!this.prevButton || !this.nextButton || !this.splide) return;
    const index = this.splide.index;
    const maxIndex = this.splide.length - this.splide.options.perPage;

    if (this.splide.options.type !== 'loop') {
      if (this.prevButton instanceof HTMLElement) {
        if (index <= 0) {
          this.prevButton.style.opacity = '0.5';
          this.prevButton.style.pointerEvents = 'none';
        } else {
          this.prevButton.style.opacity = '1';
          this.prevButton.style.pointerEvents = 'auto';
        }
      }
      if (this.nextButton instanceof HTMLElement) {
        if (index >= maxIndex) {
          this.nextButton.style.opacity = '0.5';
          this.nextButton.style.pointerEvents = 'none';
        } else {
          this.nextButton.style.opacity = '1';
          this.nextButton.style.pointerEvents = 'auto';
        }
      }
    }
  }

  attachScrollbar() {
    if (!this.splide || !this.splideElement) return;
    const section = this.splideElement.closest('.section-video-reviews');
    if (!section) return;

    const thumb = section.querySelector('.video-reviews-scrollbar-thumb');
    if (!thumb) return;

    const updateThumb = () => {
      const total = this.splide.length;
      const index = this.splide.index;
      const thumbWidth = Math.max((1 / total) * 100, 10);
      const progress = total > 1 ? index / (total - 1) : 0;
      const maxLeft = 100 - thumbWidth;
      thumb.style.width = thumbWidth + '%';
      thumb.style.left = (progress * maxLeft) + '%';
    };

    updateThumb();
    this.splide.on('move', updateThumb);
    this.splide.on('resize', updateThumb);
  }

  /**
   * Click-to-play for video cards. The card initially shows just a YouTube
   * thumbnail + play-button overlay (no iframe). On a real click — distinguished
   * from a drag/swipe via a pixel-distance threshold — the iframe is injected
   * with autoplay. Drags are passed through to Splide so the carousel can
   * still be swiped horizontally.
   */
  attachVideoClickToPlay() {
    if (!this.splideElement) return;
    const section = this.splideElement.closest('.section-video-reviews');
    if (!section) return;

    const wrappers = section.querySelectorAll('.video-review-card__video-wrapper');
    wrappers.forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      if (wrapper.dataset.clickInit === '1') return;
      wrapper.dataset.clickInit = '1';

      let startX = 0;
      let startY = 0;
      let dragged = false;
      const DRAG_THRESHOLD = 8; // pixels

      const onDown = (e) => {
        const point = e.touches ? e.touches[0] : e;
        startX = point.clientX;
        startY = point.clientY;
        dragged = false;
      };

      const onMove = (e) => {
        const point = e.touches ? e.touches[0] : e;
        if (Math.abs(point.clientX - startX) > DRAG_THRESHOLD ||
            Math.abs(point.clientY - startY) > DRAG_THRESHOLD) {
          dragged = true;
        }
      };

      const onUp = () => {
        if (dragged) return; // it was a swipe; ignore
        this.playVideo(wrapper);
      };

      wrapper.addEventListener('mousedown', onDown);
      wrapper.addEventListener('mousemove', onMove);
      wrapper.addEventListener('mouseup', onUp);
      wrapper.addEventListener('touchstart', onDown, { passive: true });
      wrapper.addEventListener('touchmove', onMove, { passive: true });
      wrapper.addEventListener('touchend', onUp);
    });
  }

  /**
   * Play the clicked card's video. Mode is read from data-playback-mode:
   *   "modal"  -> opens the per-block .video-modal (fullscreen overlay)
   *   "inline" -> injects the YouTube iframe inside the card itself
   */
  playVideo(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return;

    const id = wrapper.dataset.videoId;
    if (!id) return;
    const mode = wrapper.dataset.playbackMode || 'modal';
    const title = wrapper.dataset.videoTitle || 'Video review';
    const embedUrl =
      'https://www.youtube-nocookie.com/embed/' + id +
      '?autoplay=1&rel=0&modestbranding=1&playsinline=1';

    if (mode === 'inline') {
      this.playInline(wrapper, embedUrl, title);
    } else {
      this.openModal(wrapper, embedUrl, title);
    }
  }

  /**
   * Inline playback — inject the iframe inside the card, on top of the
   * thumbnail. Uses the YouTube IFrame Player API so we can detect when
   * the user pauses (or the video ends) and revert to the poster + play
   * button view. The .is-playing class hides the underlying thumb/button.
   */
  playInline(wrapper, embedUrl, title) {
    if (wrapper.classList.contains('is-playing')) return;

    const id = wrapper.dataset.videoId;
    if (!id) return;

    // Build a YouTube embed URL with the JS API enabled so we can listen
    // for state changes. The origin param is required by the API for the
    // postMessage handshake. enablejsapi=1 is the key flag.
    const origin = encodeURIComponent(window.location.origin);
    // mute=1 is required for browsers to honor autoplay (Chrome/Safari/iOS
    // block autoplay-with-sound). Without it the user sees YouTube's big
    // play button and has to click it again — defeating the inline UX.
    const apiUrl =
      'https://www.youtube-nocookie.com/embed/' + id +
      '?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=' + origin;

    const iframeId = 'vrIframe-' + Math.random().toString(36).slice(2, 10);
    const iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.className = 'video-review-card__iframe';
    iframe.title = title;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    iframe.setAttribute('allowfullscreen', '');
    iframe.src = apiUrl;

    wrapper.appendChild(iframe);
    wrapper.classList.add('is-playing');

    // Once the YT API is loaded, attach a player to this iframe.
    VideoReviewsSlider.loadYouTubeAPI().then(() => {
      // Track whether we've actually played yet — so brief PAUSED transitions
      // before playback truly begins (buffering, seek, etc.) don't trigger
      // an accidental revert to the poster.
      let hasPlayed = false;
      // @ts-ignore - YT is the global from www.youtube.com/iframe_api
      new window.YT.Player(iframeId, {
        events: {
          // Explicitly call playVideo() on ready — guarantees playback
          // starts immediately even if the autoplay URL param is ignored
          // by the browser. The video is muted, so this is allowed.
          onReady: (event) => {
            try { event.target.mute(); } catch (e) {}
            try { event.target.playVideo(); } catch (e) {}
          },
          onStateChange: (event) => {
            // YT.PlayerState: PLAYING = 1, PAUSED = 2, ENDED = 0
            if (event.data === 1) hasPlayed = true;
            if ((event.data === 2 || event.data === 0) && hasPlayed) {
              this.revertInline(wrapper, event.target);
            }
          }
        }
      });
    });
  }

  /**
   * Remove the inline iframe and return the card to its poster + play
   * button state. Called when the user pauses or the video ends.
   */
  revertInline(wrapper, player) {
    if (!(wrapper instanceof HTMLElement)) return;
    if (!wrapper.classList.contains('is-playing')) return;

    try { if (player && typeof player.destroy === 'function') player.destroy(); } catch (e) {}

    const iframe = wrapper.querySelector('.video-review-card__iframe');
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    wrapper.classList.remove('is-playing');
  }

  /**
   * Lazy-load the YouTube IFrame API. Returns a promise that resolves
   * when window.YT.Player is ready. Caches the promise so we never load
   * the script more than once.
   */
  static loadYouTubeAPI() {
    if (VideoReviewsSlider._ytApiPromise) return VideoReviewsSlider._ytApiPromise;
    VideoReviewsSlider._ytApiPromise = new Promise((resolve) => {
      // Already loaded?
      // @ts-ignore
      if (window.YT && window.YT.Player) {
        resolve();
        return;
      }
      // Hook the API's ready callback (chained with any existing one).
      // @ts-ignore
      const prev = window.onYouTubeIframeAPIReady;
      // @ts-ignore
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prev === 'function') { try { prev(); } catch (e) {} }
        resolve();
      };
      // Inject the API script (only once)
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const s = document.createElement('script');
        s.src = 'https://www.youtube.com/iframe_api';
        s.async = true;
        document.head.appendChild(s);
      }
    });
    return VideoReviewsSlider._ytApiPromise;
  }

  /**
   * Modal playback — open the per-block modal (id = data-modal-id). Each
   * block has its own modal so multiple cards never conflict.
   */
  openModal(wrapper, embedUrl, title) {
    const modalId = wrapper.dataset.modalId;
    if (!modalId) return;

    const modal = document.getElementById(modalId);
    const iframe = modal && modal.querySelector('.video-modal-iframe');
    if (!modal || !iframe) return;

    iframe.setAttribute('title', title);
    iframe.src = embedUrl;

    modal.classList.add('visible');
    document.body.style.overflow = 'hidden';

    this.attachModalCloseHandlers(modal);
  }

  attachModalCloseHandlers(modal) {
    if (!(modal instanceof HTMLElement)) return;
    if (modal.dataset.closeBound === '1') return;
    modal.dataset.closeBound = '1';

    const iframe = modal.querySelector('.video-modal-iframe');
    const closeBtn = modal.querySelector('.video-modal-close');

    const close = () => {
      modal.classList.remove('visible');
      document.body.style.overflow = '';
      if (iframe) iframe.setAttribute('src', '');
    };

    if (closeBtn) closeBtn.addEventListener('click', close);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('visible')) close();
    });
  }

  destroy() {
    if (this.splide) {
      this.splide.destroy();
    }
  }
}

function initAllVideoReviewsSliders() {
  const carousels = document.querySelectorAll('.section-video-reviews .splide:not(.is-initialized)');
  carousels.forEach((carousel) => {
    new VideoReviewsSlider(carousel);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllVideoReviewsSliders);
} else {
  initAllVideoReviewsSliders();
}

// Re-init on theme-editor section reload
document.addEventListener('shopify:section:load', function(event) {
  var section = event.target;
  if (section && section.querySelector('.video-reviews-splide')) {
    var carousels = section.querySelectorAll('.video-reviews-splide');
    carousels.forEach(function(carousel) {
      new VideoReviewsSlider(carousel);
    });
  }
});

window.VideoReviewsSlider = VideoReviewsSlider;
window.initAllVideoReviewsSliders = initAllVideoReviewsSliders;
}
